import React, { Component } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import NavBar from "../../components/navbar";
import { Link } from "react-router-dom";
import CalendarCoordinatorPagePresenter from "../calendarcoordinator/CalendarCoordinatorPagePresenter";
import {
  countObjectUseCase,
  deleteObjectUseCase,
  findObjectUseCase,
  upsertUseCase,
} from "../../usecases/object";
import { exportCSVUseCase } from "../../usecases/csv/usecases";
import {
  addSchemaUseCase,
  deleteSchemaUseCase,
  updateSchemaUseCase,
} from "../../usecases/schema/usecases";

// Initialize moment localizer
moment.suppressDeprecationWarnings = true;
const localizer = momentLocalizer(moment);

class MyCalendar extends Component {
  constructor(props) {
    super(props);
    this.presenter = new CalendarCoordinatorPagePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase(),
      exportCSVUseCase(),
      addSchemaUseCase(),
      updateSchemaUseCase(),
      deleteSchemaUseCase()
    );

    this.state = {
      events: this.props.initialEvents || [],
      view: "month",
      coordinatorId: this.props.coordinatorId || "default-coordinator-id",
      coordinatorUsername: "Loading...",
    };
  }

  getCollectionName = () => "calendar";
  getCurrentUser = () => ({ id: this.state.coordinatorId });
  getKeys = () => [];
  setTotal = (total) => console.log("Total events:", total);
  setObjects = (objects) => console.log("Set objects:", objects);
  showError = (error) => console.error("Error:", error);

  debugEvents = () => {
    if (!Array.isArray(this.state.events) || this.state.events.length === 0) {
      console.log("No events to debug");
      return;
    }

    console.group("Calendar Events Debug");
    this.state.events.forEach((event, index) => {
      console.log(`Event ${index + 1}: ${event.title}`);
      console.log(`  Start: ${event.start.toLocaleString()}`);
      console.log(`  End: ${event.end.toLocaleString()}`);
      const isStartValid = !isNaN(event.start.getTime());
      const isEndValid = !isNaN(event.end.getTime());
      console.log(`  Valid dates? Start: ${isStartValid}, End: ${isEndValid}`);
      console.log(
        `  Start time: ${event.start.getHours()}:${String(
          event.start.getMinutes()
        ).padStart(2, "0")}`
      );
      console.log(
        `  End time: ${event.end.getHours()}:${String(
          event.end.getMinutes()
        ).padStart(2, "0")}`
      );
    });
    console.groupEnd();
  };

  fetchCoordinatorUsername = async (coordinatorId) => {
    try {
      const userCollection = "users";
      const query = { where: { id: coordinatorId } };
      const users = await this.presenter.findObjectUseCase.execute(
        userCollection,
        query
      );
      if (users && users.length > 0) {
        const username =
          users[0].username || users[0].name || users[0].fullName;
        this.setState({ coordinatorUsername: username });
      } else {
        this.setState({ coordinatorUsername: "Unknown Coordinator" });
      }
    } catch (error) {
      console.error("Error fetching coordinator username:", error);
      this.setState({ coordinatorUsername: "Unknown Coordinator" });
    }
  };

  componentDidMount() {
    const pathParts = window.location.pathname.split("/");
    const coordinatorId = pathParts[pathParts.length - 1];
    const validCoordinatorId =
      coordinatorId !== "calendar" ? coordinatorId : null;
    console.log(
      "Calendar initialized with coordinator ID:",
      validCoordinatorId
    );

    if (validCoordinatorId) {
      this.setState({ coordinatorId: validCoordinatorId }, () => {
        this.fetchEvents().then(() => this.debugEvents());
        this.fetchCoordinatorUsername(validCoordinatorId);
      });
    } else {
      console.error("No valid coordinator ID found in URL");
    }

    const style = document.createElement("style");
    style.innerHTML = `
    .rbc-month-view{
      background-color: #f8f8f8 !important;
    }

    .rbc-time-view {
      background-color: #f8f8f8 !important;
    }

    .rbc-day-view .rbc-time-view,
    .rbc-work-week-view .rbc-time-view {
      background-color: #f8f8f8 !important;
    }

    .rbc-agenda-view {
      background-color: #f8f8f8 !important;
    }

    .rbc-toolbar button {
      background-color: #f8f8f8 !important;
    }
    `;
    document.head.appendChild(style);
  }

  createEventObject = (event) => {
    try {
      if (!event.startDate || !event.endDate) {
        console.error("Missing event date fields:", event);
        return null;
      }

      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error("Invalid date format for event:", event);
        return null;
      }

      let startDateTime, endDateTime;
      let allDay = false;

      if (event.startTime) {
        const timeFormat = event.startTime.includes(":")
          ? event.startTime
          : `${event.startTime}:00`;
        const [startHours, startMinutes] = timeFormat
          .split(":")
          .map((num) => parseInt(num, 10));
        startDateTime = new Date(startDate);
        startDateTime.setHours(startHours, startMinutes, 0, 0);
      } else {
        startDateTime = new Date(startDate);
        startDateTime.setHours(0, 0, 0, 0);
        allDay = true;
      }

      if (event.endTime) {
        const timeFormat = event.endTime.includes(":")
          ? event.endTime
          : `${event.endTime}:00`;
        const [endHours, endMinutes] = timeFormat
          .split(":")
          .map((num) => parseInt(num, 10));
        endDateTime = new Date(endDate);
        endDateTime.setHours(endHours, endMinutes, 0, 0);

        if (
          endDate.toDateString() === startDate.toDateString() &&
          endDateTime <= startDateTime
        ) {
          endDateTime = new Date(startDateTime);
          endDateTime.setMinutes(endDateTime.getMinutes() + 30);
        }
      } else {
        endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        allDay = true;
      }

      allDay =
        !event.startTime || !event.endTime
          ? true
          : endDate.toDateString() !== startDate.toDateString()
          ? false
          : allDay;

      return {
        id:
          event.id ||
          `event-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        title: event.description || event.title || "Unnamed Event",
        start: startDateTime,
        end: endDateTime,
        allDay: allDay,
        resource: event.resourceId || null,
        originalEvent: event,
      };
    } catch (error) {
      console.error("Error processing event:", error, event);
      return null;
    }
  };

  processEventsArray = (eventsArray) => {
    if (!Array.isArray(eventsArray)) {
      console.error("Expected array but got:", typeof eventsArray);
      return [];
    }
    return eventsArray
      .map(this.createEventObject)
      .filter((event) => event !== null);
  };

  processEventsObject = (eventsObject) => {
    const formattedEvents = [];
    Object.keys(eventsObject).forEach((dateKey) => {
      const dateEvents = eventsObject[dateKey];
      if (Array.isArray(dateEvents)) {
        dateEvents.forEach((event) => {
          const formattedEvent = this.createEventObject(event);
          if (formattedEvent) formattedEvents.push(formattedEvent);
        });
      }
    });
    return formattedEvents;
  };

  fetchEvents = async () => {
    try {
      const coordinatorUser = { id: this.state.coordinatorId };
      console.log(
        "Fetching events with coordinator ID:",
        this.state.coordinatorId
      );
      const eventsData = await this.presenter.getAllEvents(coordinatorUser);
      console.log("Loaded events data:", eventsData);

      let formattedEvents = [];
      if (Array.isArray(eventsData)) {
        formattedEvents = this.processEventsArray(eventsData);
      } else if (typeof eventsData === "object") {
        formattedEvents = this.processEventsObject(eventsData);
      }

      console.log("Final formatted events:", formattedEvents);
      this.setState({ events: formattedEvents }, this.debugEvents);
    } catch (error) {
      console.error("Error loading events:", error);
      this.setState({ events: [] });
    }
  };

  eventStyleGetter = (event, start, end, isSelected) => {
    const style = {
      backgroundColor: "#3174ad",
      borderRadius: "3px",
      opacity: 0.85,
      color: "white",
      border: "0",
      display: "block",
      fontSize: "0.85em",
      overflow: "hidden",
      textOverflow: "ellipsis",
      padding: "2px 5px",
      margin: "0",
      cursor: "pointer",
      width: "100%",
      position: "absolute",
      boxSizing: "border-box",
    };

    if (this.state.view === "month") {
      style.height = "auto";
      style.position = "relative";
    } else if (this.state.view === "agenda") {
      style.position = "relative";
      style.width = "100%";
      style.margin = "2px 0";
      style.height = "auto";
    }

    return { style };
  };

  handleViewChange = (newView) => this.setState({ view: newView });

  render() {
    const {
      height = "800px",
      hiddenHours = [1, 3, 5, 7, 9, 11, 13, 15, 17, 19, 21, 23],
    } = this.props;
    const events = Array.isArray(this.state.events) ? this.state.events : [];

    return (
      <>
        <NavBar />
        <div style={{ padding: "25px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "30px",
              marginTop: "10px",
            }}
          >
            <Link to="/recruiter/calendar">
              <div
                style={{
                  backgroundColor: "#E6E6E6",
                  borderRadius: "50%",
                  padding: "6px 11px",
                  marginRight: "10px",
                }}
              >
                <i
                  className="bi bi-arrow-left"
                  style={{
                    fontSize: "20px",
                    cursor: "pointer",
                    fontWeight: "bold",
                  }}
                ></i>
              </div>
            </Link>
            <div>
              <h2
                style={{
                  color: "#D18237",
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "bold",
                }}
              >
                Coordinator Calendar
              </h2>
              <p style={{ margin: 0, color: "#555", fontSize: "15px" }}>
                {this.state.coordinatorUsername}
              </p>
            </div>
          </div>

          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            selectable
            onSelectSlot={this.handleSelect}
            defaultView="month"
            views={{
              month: true,
              week: true,
              work_week: true,
              day: true,
              agenda: true,
            }}
            onView={this.handleViewChange}
            eventPropGetter={this.eventStyleGetter}
            step={60}
            timeslots={2}
            formats={{
              timeGutterFormat: (date, culture, localizer) =>
                hiddenHours.includes(moment(date).hour())
                  ? ""
                  : localizer.format(date, "h:mm A", culture),
              eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
                localizer.format(start, "h:mm A") +
                " - " +
                localizer.format(end, "h:mm A"),
            }}
            style={{ height }}
            min={new Date(0, 0, 0, 0, 0, 0)}
            max={new Date(0, 0, 0, 23, 59, 59)}
            dayLayoutAlgorithm="no-overlap"
            resizable
            showMultiDayTimes={true}
            slotPropGetter={(date) => ({
              style: {
                backgroundColor:
                  date.getMinutes() === 0
                    ? "rgba(240, 240, 240, 0.2)"
                    : "rgba(248, 248, 248, 0.2)",
              },
            })}
          />
        </div>
      </>
    );
  }
}

export default MyCalendar;
